import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { isByDesign } from '../api/errors';
import type { ApiFailure } from '../api/errors';
import type { TenantStatus } from '@yourstore/domain';

/**
 * The small pieces every screen uses, and the three that carry a rule.
 *
 *  * **`Failure`** renders `501 unsupported` differently from everything else,
 *    because it is not a fault (see `api/errors.ts`).
 *  * **`Confirm`** exists so that no lifecycle move is a single click, and it
 *    quotes the consequence rather than asking "are you sure?".
 *  * **`OnceBox`** shows a value that will never be shown again, says so in the
 *    CLI's own words, and drops it from memory when dismissed.
 */

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

export function StatusPill({ status }: { status: TenantStatus | string }): ReactNode {
  return <span className={`pill pill--${status}`}>{status}</span>;
}

export function DeliveryPill({ status }: { status: string }): ReactNode {
  return <span className={`pill pill--delivery-${status}`}>{status}</span>;
}

export function StatePill({ state }: { state: string }): ReactNode {
  return <span className={`pill pill--job-${state}`}>{state}</span>;
}

/* ------------------------------------------------------------------ */
/* Failure                                                             */
/* ------------------------------------------------------------------ */

/**
 * What the server said, in the server's words.
 *
 * `admin-api` writes real sentences — "products, certificates still depend on
 * sales", "Two keys are already active … revoke one first" — and puts the
 * instruction in `detail.hint`. Paraphrasing them here would produce a console
 * that is vaguer than the CLI, so the rule is: show the message, show the hint,
 * show the list the refusal named, and add nothing.
 *
 * The exception is `unsupported`. That code means a screen asked for something
 * the `admin_api` role holds no grant on — spec §6.3's "never a data browser"
 * answering from underneath the service — and it renders as a statement of the
 * design, not as an error.
 */
export function Failure({ failure }: { failure: ApiFailure }): ReactNode {
  if (isByDesign(failure)) {
    return (
      <div className="by-design" role="note">
        <h4>Not on this surface — by design</h4>
        <p>{failure.message}</p>
        <p className="by-design__rule">
          The operator console is never a data browser (spec §6.3). The <code>admin_api</code> role
          holds no grant on a tenant’s book, so this is the lock working rather than a fault to
          report.
        </p>
      </div>
    );
  }

  return (
    <div className="failure" role="alert">
      <p className="failure__message">
        <span className="failure__code">{failure.code}</span> {failure.message}
      </p>
      {failure.hint !== null ? <p className="failure__hint">{failure.hint}</p> : null}
      {failure.list.length > 0 ? (
        <ul className="failure__list">
          {failure.list.map((item) => (
            <li key={item}>
              <code>{item}</code>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Confirm                                                             */
/* ------------------------------------------------------------------ */

/**
 * A confirm that quotes the consequence.
 *
 * "Are you sure?" is a dialog people learn to click through. What an operator
 * needs before flipping a tenant to `live` is the sentence saying that from
 * that moment an agent will promise guests times on our word — so the
 * consequence is the body of the dialog, and the button says the verb rather
 * than "OK".
 */
export function Confirm({
  title,
  consequence,
  confirmLabel,
  busy,
  failure,
  onConfirm,
  onCancel,
}: {
  title: string;
  consequence: string;
  confirmLabel: string;
  busy: boolean;
  failure: ApiFailure | null;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const first = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    first.current?.focus();
  }, []);

  return (
    <div className="modal">
      <div className="modal__panel" role="dialog" aria-modal="true" aria-label={title}>
        <h3>{title}</h3>
        <p className="modal__consequence">{consequence}</p>
        {failure !== null ? <Failure failure={failure} /> : null}
        <div className="modal__actions">
          <button type="button" className="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            ref={first}
            className="button button--danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The once-only box                                                   */
/* ------------------------------------------------------------------ */

/**
 * A value that exists here and nowhere else.
 *
 * The plaintext of a key crosses the wire once (K5); only its SHA-256 hash is
 * stored, so it cannot be recovered. This box holds it in component state, in
 * the CLI's own words, and **dismissing it drops it** — the parent clears the
 * value, so there is nothing left to re-render. There is deliberately no
 * "show again": a console that could show it again would be a console that kept
 * it somewhere.
 */
export function OnceBox({
  label,
  value,
  note,
  onDismiss,
}: {
  label: string;
  value: string;
  note: string;
  onDismiss: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    // `navigator.clipboard` is absent in jsdom and over plain http. Selecting
    // the text is the fallback, and the value is on screen either way — the
    // copy button is a convenience, never the only way to get the key.
    void navigator.clipboard
      ?.writeText(value)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="once" role="alert">
      <h4>{label}</h4>
      <code className="once__value">{value}</code>
      <p className="once__note">
        This is the only time the key is shown. Only its SHA-256 hash is stored, so it cannot be
        recovered — if it is lost, revoke it and issue another.
      </p>
      <p className="once__server-note">{note}</p>
      <div className="once__actions">
        <button type="button" className="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button type="button" className="button button--primary" onClick={onDismiss}>
          I have stored it
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small things                                                        */
/* ------------------------------------------------------------------ */

export function When({ iso, empty = '—' }: { iso: string | null; empty?: string }): ReactNode {
  if (iso === null) return <span className="muted">{empty}</span>;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return <span className="muted">{iso}</span>;
  return (
    <time dateTime={iso} title={iso}>
      {parsed.toISOString().replace('T', ' ').slice(0, 16)}Z
    </time>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint === undefined ? null : <span className="field__hint">{hint}</span>}
    </label>
  );
}

export function Section({
  title,
  note,
  actions,
  children,
}: {
  title: string;
  note?: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="card">
      <header className="card__head">
        <h3>{title}</h3>
        {actions}
      </header>
      {note === undefined ? null : <p className="card__note">{note}</p>}
      {children}
    </section>
  );
}

/** Loading is a word, not a spinner: an operator read is one round trip. */
export function Loading({ what }: { what: string }): ReactNode {
  return <p className="muted">Reading {what}…</p>;
}

export function Empty({ children }: { children: ReactNode }): ReactNode {
  return <p className="empty">{children}</p>;
}
